import { useFlags } from 'launchdarkly-react-client-sdk';

const LOCAL_OVERRIDE_KEY = 'planPageUiRevamp';

/**
 * Which Plan page implementation to render.
 *
 * Resolution order, same as `utils/dashboardRevampFlag.ts` /
 * `utils/dimensionsRevampFlag.ts`:
 *   1. LaunchDarkly — whenever the `planPageUiRevamp` flag (either casing) is
 *      actually defined for the user, its value wins outright.
 *   2. `localStorage.planPageUiRevamp` — dev escape hatch, only consulted
 *      when LD has no opinion. Set it to `'false'` to fall back to the
 *      original Plan page.
 *   3. Default: the revamped Plan page.
 *
 * This used to default to `false` (`Boolean(undefined)`), the opposite of
 * its Dashboard/Dimensions siblings — on an environment where the LD flag
 * doesn't exist yet, that silently served the *original* Plan page with no
 * way to see the redesign short of hardcoding a bypass in this file.
 *
 *   localStorage.setItem('planPageUiRevamp', 'false')  // original
 *   localStorage.removeItem('planPageUiRevamp')        // back to default (revamp)
 */
export const getPlanPageUiRevampEnabled = (
  flags?: Record<string, boolean | undefined>,
): boolean => {
  const flagValue = flags?.planPageUiRevamp ?? flags?.planPageUIRevamp;
  if (flagValue !== undefined) return Boolean(flagValue);

  try {
    const override = window.localStorage.getItem(LOCAL_OVERRIDE_KEY);
    if (override !== null) return override === 'true';
  } catch {
    // localStorage can throw in private-browsing / sandboxed contexts.
  }

  return true;
};

export const usePlanPageUiRevamp = (): boolean => {
  const flags = useFlags();
  return getPlanPageUiRevampEnabled(flags);
};

/** Use with shared components that accept variant="revamp" — revamp UI only when flag is on. */
export const usePlanPageRevampVariant = (variant: 'legacy' | 'revamp' = 'legacy'): boolean => {
  const enabled = usePlanPageUiRevamp();
  return enabled && variant === 'revamp';
};
